import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { apiRequest } from "@/lib/queryClient";
import { Clock, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, differenceInCalendarDays } from "date-fns";

interface MembershipStatus {
  status: 'inactive' | 'trial' | 'active' | 'canceled' | 'past_due';
  isBeta: boolean;
  trialEndsAt: string | null;
  trialStartedAt: string | null;
  subscriptionEndsAt: string | null;
  trialDaysRemaining: number | null;
  stripeCustomerId: string | null;
  trialExpired: boolean;
}

export function TrialBanner() {
  const { data: membership, isLoading } = useQuery<MembershipStatus>({
    queryKey: ["/api/membership/status"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/membership/status");
      return res.json();
    },
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (isLoading || !membership) {
    return null;
  }

  // Beta users never see trial banners
  if (membership.isBeta) {
    return null;
  }

  // Active subscribers don't see the banner
  if (membership.status === 'active') {
    return null;
  }

  // Canceled users with expired trial don't see the banner
  if (membership.status === 'canceled') {
    // Show banner only if trial end date is in the future (canceled during trial)
    if (!membership.trialEndsAt) {
      return null;
    }
    const trialEnd = new Date(membership.trialEndsAt);
    if (trialEnd <= new Date()) {
      return null;
    }
    
    // Canceled during trial - show informational message
    return (
      <div
        className="px-4 py-2 text-sm font-medium flex items-center justify-center gap-2 bg-muted/50 text-muted-foreground border-b border-border"
        data-testid="trial-banner"
      >
        <Clock className="w-4 h-4 flex-shrink-0" />
        <span>Trial ends on {format(trialEnd, 'MMM d')} — you will not be charged.</span>
      </div>
    );
  }

  // Show expired trial banner for inactive users with expired trial
  if (membership.status === 'inactive' && membership.trialExpired) {
    return (
      <div
        className="px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium flex items-center justify-center gap-2 sm:gap-3 bg-red-500/15 text-red-400 border-b border-red-500/25"
        data-testid="trial-banner-expired"
      >
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="text-center">
          Your free trial has ended
        </span>
        <Link
          href="/settings"
          className="ml-1 px-2.5 sm:px-3 py-1 text-xs font-semibold bg-red-500/20 hover:bg-red-500/30 active:bg-red-500/40 rounded-full transition-colors whitespace-nowrap min-h-[28px] flex items-center"
          data-testid="link-subscribe-expired"
        >
          Subscribe now
        </Link>
      </div>
    );
  }

  // Only show for trial status
  if (membership.status !== 'trial') {
    return null;
  }

  const daysRemaining = membership.trialDaysRemaining ?? 0;
  
  if (daysRemaining <= 0) {
    return null;
  }

  // Calculate actual day from trialStartedAt for accurate "Day X of 14" display
  let currentDay = 1;
  if (membership.trialStartedAt) {
    const startDate = new Date(membership.trialStartedAt);
    const today = new Date();
    currentDay = Math.max(1, differenceInCalendarDays(today, startDate) + 1);
  } else {
    // Fallback to calculation from daysRemaining
    currentDay = 14 - daysRemaining + 1;
  }
  
  const isWarningPhase = daysRemaining <= 3;

  // Early trial (days 1-11): Neutral informational - entire banner is clickable
  if (!isWarningPhase) {
    return (
      <Link href="/settings" data-testid="trial-banner-link">
        <div
          className="px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium flex items-center justify-center gap-2 bg-blue-500/10 text-blue-400 border-b border-blue-500/20 cursor-pointer hover:bg-blue-500/15 transition-colors"
          data-testid="trial-banner"
        >
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>Free trial — Day {currentDay} of 14</span>
        </div>
      </Link>
    );
  }

  // Final 3 days: Warning with CTA - entire banner is clickable
  return (
    <Link href="/settings" data-testid="trial-banner-warning-link">
      <div
        className="px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium flex items-center justify-center gap-2 sm:gap-3 bg-amber-500/15 text-amber-400 border-b border-amber-500/25 cursor-pointer hover:bg-amber-500/20 transition-colors"
        data-testid="trial-banner-warning"
      >
        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
        <span className="text-center">
          Your free trial ends in {daysRemaining} day{daysRemaining === 1 ? '' : 's'}
        </span>
        <span
          className="ml-1 px-2.5 sm:px-3 py-1 text-xs font-semibold bg-amber-500/20 rounded-full whitespace-nowrap min-h-[28px] flex items-center"
        >
          Subscribe now
        </span>
      </div>
    </Link>
  );
}
